import { inject, Injectable } from '@angular/core';
import { DATE_FORMAT } from '../models/constants/input.constants';
import { TranslateService } from '@ngx-translate/core';
import { UserMinimal } from '../models/user-minimal.model';
import { TransportOrderView } from '../models/transport-order-view.model';
import { TransportConstraintsService } from './transport-constraints.service';

@Injectable({ providedIn: 'root' })
export class MailService {
  readonly #translateService = inject(TranslateService);
  readonly #transportConstraintsService = inject(TransportConstraintsService);

  public addDocumentBody(transportOrderView: TransportOrderView | undefined, transporterCompanyName: string | undefined, user: UserMinimal | undefined): string {
    let body: string = '';
    body += this.addMainContent(transportOrderView, transporterCompanyName);
    body += this.addCommonDataBody(transportOrderView, user);
    return body;
  }

  public addDocumentAdrBody(transportOrderView: TransportOrderView | undefined, transporterCompanyName: string | undefined, user: UserMinimal): string {
    let body: string = '';
    body += this.addMainContentADR(transportOrderView, transporterCompanyName);
    body += this.addCommonDataBody(transportOrderView, user);
    return body;
  }

  public addCommonDataBody(transportOrderView: TransportOrderView | undefined, user: UserMinimal | undefined): string {
    let body: string = '';
    body += this.addCustomerBlock(user);
    body += this.addMerchandiseBlock(transportOrderView);
    body += this.addExpeditionBlock(transportOrderView);
    body += this.addDeliveryBlock(transportOrderView);
    body += this.addVariousBlock(transportOrderView);
    body += this.addRateBlock(transportOrderView);
    return body;
  }

  public addLineBreaker(): string {
    return '%0D%0A%0D%0A';
  }

  public addTitle(title: string): string {
    return title + ' :' + this.addLineBreaker();
  }

  public addSubTitle(title: string): string {
    return '  ' + title + ' : ';
  }

  public addSubBlock(block: string): string {
    return '  ' + block;
  }

  public addMainContent(transportOrderView: TransportOrderView | undefined, transporterCompanyName: string | undefined): string {
    let block: string = this.#translateService.instant('mailto.add_document.body', {
      companyName: transporterCompanyName,
      transportOrderId: transportOrderView?.id,
    });
    block += this.addLineBreaker();
    return block;
  }

  public addMainContentADR(transportOrderView: TransportOrderView | undefined, transporterCompanyName: string | undefined): string {
    let block: string = this.#translateService.instant('mailto.add_document_adr.body', {
      companyName: transporterCompanyName,
      transportOrderId: transportOrderView?.id,
    });
    block += this.addLineBreaker();
    return block;
  }

  public addCustomerBlock(user: UserMinimal | undefined): string {
    let block: string = this.addTitle(this.#translateService.instant('mailto.bloc_ot.bloc_customer.title'));
    block += this.addSubTitle(this.#translateService.instant('mailto.bloc_ot.bloc_customer.sub_title1')) + user?.firstName + ' ' + user?.lastName + this.addLineBreaker();
    block += this.addSubTitle(this.#translateService.instant('mailto.bloc_ot.bloc_customer.sub_title2'));
    block += user?.companyName ?? '';
    block += this.addLineBreaker();
    block += this.addSubTitle(this.#translateService.instant('mailto.bloc_ot.bloc_customer.sub_title3')) + user?.phone + this.addLineBreaker();
    block += this.addSubTitle(this.#translateService.instant('mailto.bloc_ot.bloc_customer.sub_title4')) + user?.email + this.addLineBreaker();
    block += this.addLineBreaker();
    return block;
  }

  public addMerchandiseBlock(transportOrderView: TransportOrderView | undefined): string {
    let block: string = this.addTitle(this.#translateService.instant('mailto.bloc_ot.bloc_merchandise.title'));
    block += this.addSubBlock(this.#translateService.instant('transport-typology.' + transportOrderView?.transportTypology)) + this.addLineBreaker();
    transportOrderView?.items?.forEach((it: any) => {
      block +=
        this.addSubBlock(
          this.#translateService.instant('mailto.bloc_ot.bloc_merchandise.sub_block1', {
            quantity: it.quantity,
            handlingType: this.#translateService.instant('mailto.HandlingType.' + it.handlingType),
            width: it.width,
            length: it.length,
            maxHeight: it.maxHeight,
            weight: it.weight,
          })
        ) + this.addLineBreaker();
    });
    block += this.addLineBreaker();
    return block;
  }

  public addExpeditionBlock(transportOrderView: TransportOrderView | undefined): string {
    let block: string = this.addTitle(this.#translateService.instant('mailto.bloc_ot.bloc_expedition.title'));
    block += this.addSubTitle(this.#translateService.instant('mailto.bloc_ot.bloc_expedition.sub_title1'));
    block += transportOrderView?.pickupAddressStreetNumber ?? '';
    block += ' ';
    block += transportOrderView?.pickupAddressStreet ?? '';
    block += ' ';
    block += transportOrderView?.pickupAddressZipCode ?? '';
    block += ' ';
    block += transportOrderView?.pickupAddressCity ?? '';
    block += ' ';
    block += transportOrderView?.pickupAddressCountry ?? '';
    block += this.addLineBreaker();
    block +=
      this.addSubBlock(
        this.#translateService.instant('mailto.bloc_ot.bloc_expedition.sub_block1', {
          pickupDate: transportOrderView?.pickupDate?.format(DATE_FORMAT),
          pickupTimeslotStart: transportOrderView?.pickupTimeslotStart,
          pickupTimeslotEnd: transportOrderView?.pickupTimeslotEnd,
        })
      ) + this.addLineBreaker();
    block += this.addSubTitle(this.#translateService.instant('mailto.bloc_ot.bloc_expedition.sub_title2'));
    block += transportOrderView?.pickupContactFirstName ?? '';
    block += ' ';
    block += transportOrderView?.pickupContactLastName ?? '';
    block += ' ';
    block += transportOrderView?.pickupContactMail ?? '';
    block += ' ';
    block += transportOrderView?.pickupContactPhone ?? '';
    block += this.addLineBreaker();
    block += this.addSubTitle(this.#translateService.instant('mailto.bloc_ot.bloc_expedition.sub_title3'));
    block += transportOrderView
      ? this.addSubBlock(
          this.#transportConstraintsService.formatConstraints(transportOrderView, {
            pickup: true,
            delivery: false,
            inline: true,
          })
        ) + this.addLineBreaker()
      : '';
    block += this.addLineBreaker();
    return block;
  }

  public addDeliveryBlock(transportOrderView: TransportOrderView | undefined): string {
    let block: string = this.addTitle(this.#translateService.instant('mailto.bloc_ot.bloc_delivery.title'));
    block += this.addSubTitle(this.#translateService.instant('mailto.bloc_ot.bloc_delivery.sub_title1'));
    block += transportOrderView?.deliveryAddressStreetNumber + ' ';
    block += transportOrderView?.deliveryAddressStreet + ' ';
    block += transportOrderView?.deliveryAddressZipCode + ' ';
    block += transportOrderView?.deliveryAddressCity + ' ';
    block += transportOrderView?.deliveryAddressCountry + this.addLineBreaker();
    block +=
      this.addSubBlock(
        this.#translateService.instant('mailto.bloc_ot.bloc_delivery.sub_block1', {
          deliveryDate: transportOrderView?.pickupDate?.format(DATE_FORMAT),
          deliveryTimeslotStart: transportOrderView?.deliveryTimeslotStart,
          deliveryTimeslotEnd: transportOrderView?.deliveryTimeslotEnd,
        })
      ) + this.addLineBreaker();
    block += this.addSubTitle(this.#translateService.instant('mailto.bloc_ot.bloc_delivery.sub_title2'));
    block += transportOrderView?.deliveryContactFirstName ?? '';
    block += ' ';
    block += transportOrderView?.deliveryContactLastName ?? '';
    block += ' ';
    block += transportOrderView?.deliveryContactMail ?? '';
    block += ' ';
    block += transportOrderView?.deliveryContactPhone ?? '';
    block += this.addLineBreaker();
    block += this.addSubTitle(this.#translateService.instant('mailto.bloc_ot.bloc_delivery.sub_title3'));
    block += transportOrderView
      ? this.addSubBlock(
          this.#transportConstraintsService.formatConstraints(transportOrderView, {
            pickup: false,
            delivery: true,
            inline: true,
          })
        ) + this.addLineBreaker()
      : '';
    block += this.addLineBreaker();
    return block;
  }

  public addVariousBlock(transportOrderView: TransportOrderView | undefined): string {
    let block: string = this.addTitle(this.#translateService.instant('mailto.bloc_ot.bloc_various.title'));

    if (transportOrderView?.goodsValue && transportOrderView?.goodsValue > 0) {
      block += this.addSubTitle(this.#translateService.instant('mailto.bloc_ot.bloc_various.sub_title1'));
      block += transportOrderView?.insurance + ' €';
    } else {
      block += this.addSubBlock(this.#translateService.instant('mailto.bloc_ot.bloc_various.sub_block1'));
    }
    block += this.addLineBreaker();
    block += this.addLineBreaker();
    return block;
  }

  public addRateBlock(transportOrderView: TransportOrderView | undefined): string {
    let block: string = this.addTitle(this.#translateService.instant('mailto.bloc_ot.bloc_rate_block.title'));
    block += this.addSubTitle(this.#translateService.instant('mailto.bloc_ot.bloc_rate_block.sub_title1'));
    block += transportOrderView?.priceHT + ' € HT' + this.addLineBreaker();
    block += this.addLineBreaker();
    return block;
  }
}
